import { Outlet, Navigate, matchPath, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { Suspense, useEffect, useState } from 'react';
import { HelpGuideTourRunner } from '@/components/help/HelpGuideTourRunner';
import { useAuthorizationStore } from '@/stores/authorization';
import { canUseMultiEstablishmentContext, useAuthStore } from '@/stores/auth';
import { EstablishmentContextSelect } from '@/components/context/EstablishmentContextSelect';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { reportFrontendDiagnostic } from '@/lib/frontendDiagnostics';

const SINGLE_ESTABLISHMENT_CONTEXT_PATTERNS = [
  '/establishment/home',
  '/access-control/facesign-analysis-desk',
  '/access-control/facesign-events',
  '/my-establishment/*',
  '/dashboard/*',
  '/integrations/*',
  '/teleattendance',
  '/teleattendance/room/:schedulePlanningProfessionalId',
  '/scale-management/scheduled-shift',
  '/scale-management/scheduled-shift/register',
  '/scale-management/scheduled-shift-plan',
  '/scale-management/records',
  '/scale-management/facesign-intercurrences',
  '/scale-management/record-authorization',
  '/scale-management/shift-exchanges',
  '/scale-management/company-calendar',
  '/scale-management/company-overview',
  '/my-company/document-management',
];

const AppLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-muted-foreground">Carregando...</p>
    </div>
  </div>
);

function requiresSingleEstablishmentContext(pathname: string): boolean {
  return SINGLE_ESTABLISHMENT_CONTEXT_PATTERNS.some(
    (pattern) => matchPath({ path: pattern, end: true }, pathname) !== null,
  );
}

function hasSingleEstablishmentContext(
  user: ReturnType<typeof useAuthStore.getState>['user'],
): boolean {
  const ids = user?.establishment_config_ids ?? [];
  const contextType = user?.context_type ?? '';

  return (
    Boolean(user?.establishment_config_id) &&
    ids.length <= 1 &&
    contextType !== 'multi_establishment' &&
    contextType !== 'all_establishments'
  );
}

const EstablishmentContextContentOverlay = () => {
  const { toast } = useToast();
  const { user, isSelectingContext, selectEstablishmentContext } =
    useAuthStore();
  const establishments = user?.establishments ?? [];
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (!selected && establishments.length > 0) {
      setSelected(establishments[0].establishment_config_id);
    }
  }, [establishments, selected]);

  const handleConfirm = async () => {
    if (!selected) return;

    try {
      await selectEstablishmentContext(selected);
      toast({
        title: 'Estabelecimento selecionado',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Não foi possível selecionar o estabelecimento',
        variant: 'destructive',
      });
    }
  };

  if (establishments.length === 0) return null;

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/85 p-6 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-lg border bg-card p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <h2 className="text-base font-semibold text-card-foreground">
              Selecione um estabelecimento
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Esta tela depende de um estabelecimento específico. Escolha um
              estabelecimento para continuar.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="min-w-0 flex-1">
            <EstablishmentContextSelect
              value={selected}
              onChange={setSelected}
              placeholder="Selecione um estabelecimento"
              className="text-left"
              includeAllOption={false}
            />
          </div>
          <Button
            onClick={handleConfirm}
            disabled={!selected || isSelectingContext}
          >
            {isSelectingContext ? 'Selecionando...' : 'Continuar'}
          </Button>
        </div>
      </div>
    </div>
  );
};
export const MainLayout = () => {
  const location = useLocation();
  const modules = useAuthorizationStore((s) => s.payload?.modules);
  const selectedModuleKey = useAuthorizationStore((s) => s.selectedModuleKey);
  const user = useAuthStore((s) => s.user);
  const shouldShowContextOverlay =
    selectedModuleKey === 'medical_company' &&
    canUseMultiEstablishmentContext(user) &&
    requiresSingleEstablishmentContext(location.pathname) &&
    !hasSingleEstablishmentContext(user);

  useEffect(() => {
    if (modules && modules.length > 1 && !selectedModuleKey) {
      reportFrontendDiagnostic(
        'module_gateway_redirect',
        new Error('No module is selected for a multi-module user'),
        {
          from: location.pathname,
          to: '/modules',
          reason: 'main_layout_missing_selected_module',
        },
      );
    }
  }, [location.pathname, modules, selectedModuleKey]);

  // If user has multiple modules but none selected, force gateway selection
  if (modules && modules.length > 1 && !selectedModuleKey) {
    return <Navigate to="/modules" replace />;
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />

      <main className="flex-1 overflow-hidden flex flex-col">
        <Topbar />
        <HelpGuideTourRunner />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="relative h-full overflow-auto p-6"
        >
          <Suspense fallback={<AppLoading />}>
            {shouldShowContextOverlay ? (
              <EstablishmentContextContentOverlay />
            ) : (
              <Outlet />
            )}
          </Suspense>
        </motion.div>
      </main>
    </div>
  );
};
